import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListChecks, ChevronDown, ChevronUp, Pencil, Plus, Trash2, RotateCcw, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_OPEN = "tc_consignment_workflow_open";

// Reference checklist shown to the admin while reviewing a consignment submission. Editable, stored server-side.
export const ConsignmentWorkflowCard = ({ getAuthHeader }) => {
  const [steps, setSteps] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [open, setOpen] = useState(() => localStorage.getItem(LS_OPEN) !== "0");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/consignment-workflow`, getAuthHeader()).then((r) => {
      setSteps(r.data.steps || []);
      setIsDefault(!!r.data.is_default);
    }).catch(() => {});
  }, [getAuthHeader]);

  const toggleOpen = () => { setOpen((v) => { localStorage.setItem(LS_OPEN, v ? "0" : "1"); return !v; }); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/consignment-workflow`, { steps: draft }, getAuthHeader());
      setSteps(r.data.steps); setIsDefault(false); setEditing(false);
      toast.success("Workflow saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save workflow");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the workflow to the default steps?")) return;
    const r = await axios.delete(`${API}/admin/consignment-workflow`, getAuthHeader());
    setSteps(r.data.steps); setIsDefault(true); setEditing(false);
  };

  const setField = (i, k, v) => setDraft((d) => d.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  return (
    <div className="mx-6 mt-4 rounded-xl border border-[#8B5CF6]/25 bg-gradient-to-br from-[#F5F3FF] to-white" data-testid="consignment-workflow-card">
      <button type="button" onClick={toggleOpen} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="consignment-workflow-toggle">
        <ListChecks className="w-4 h-4 text-[#8B5CF6]" />
        <span className="font-semibold text-sm text-[#1A1A2E]">Consignment workflow</span>
        <span className="text-xs text-gray-500">· {steps.length} steps{isDefault ? "" : " · customized"}</span>
        <span className="ml-auto text-gray-400">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>

      {open && !editing && (
        <div className="px-4 pb-3">
          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`workflow-step-${i}`}>
                <button type="button" onClick={() => setDone((d) => ({ ...d, [i]: !d[i] }))} className={`mt-0.5 w-5 h-5 rounded-full flex-shrink-0 text-[11px] font-bold flex items-center justify-center border ${done[i] ? "bg-[#10B981] border-[#10B981] text-white" : "border-[#8B5CF6]/40 text-[#8B5CF6]"}`} aria-label={`Step ${i + 1}`} data-testid={`workflow-step-check-${i}`}>
                  {done[i] ? "✓" : i + 1}
                </button>
                <div className={done[i] ? "opacity-50 line-through" : ""}>
                  <p className="text-sm font-medium text-[#1A1A2E] leading-tight">{s.title}</p>
                  {s.detail && <p className="text-xs text-gray-600 leading-snug mt-0.5">{s.detail}</p>}
                </div>
              </li>
            ))}
          </ol>
          <div className="flex items-center gap-3 mt-3">
            <button type="button" onClick={() => { setDraft(steps.map((s) => ({ ...s }))); setEditing(true); }} className="flex items-center gap-1 text-xs text-[#8B5CF6] font-medium" data-testid="workflow-edit-btn"><Pencil className="w-3 h-3" /> Edit steps</button>
            {Object.values(done).some(Boolean) && <button type="button" onClick={() => setDone({})} className="text-xs text-gray-500" data-testid="workflow-clear-checks">Clear checks</button>}
          </div>
        </div>
      )}

      {open && editing && (
        <div className="px-4 pb-4 space-y-3">
          {draft.map((s, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-2 space-y-1" data-testid={`workflow-edit-step-${i}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#8B5CF6] w-4">{i + 1}</span>
                <input value={s.title} onChange={(e) => setField(i, "title", e.target.value)} placeholder="Step title" className="flex-1 text-sm font-medium px-2 py-1 border rounded-md" data-testid={`workflow-edit-title-${i}`} />
                <button type="button" onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600" aria-label="Remove step" data-testid={`workflow-remove-step-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={s.detail || ""} onChange={(e) => setField(i, "detail", e.target.value)} placeholder="What to do in this step" rows={2} className="w-full text-xs px-2 py-1 border rounded-md resize-none" data-testid={`workflow-edit-detail-${i}`} />
            </div>
          ))}
          <button type="button" onClick={() => setDraft((d) => [...d, { title: "", detail: "" }])} className="flex items-center gap-1 text-xs text-[#8B5CF6] font-medium" data-testid="workflow-add-step"><Plus className="w-3 h-3" /> Add step</button>
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={save} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#8B5CF6] text-white text-xs font-semibold disabled:opacity-60" data-testid="workflow-save-btn"><Save className="w-3 h-3" /> Save</button>
            <button type="button" onClick={() => setEditing(false)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold" data-testid="workflow-cancel-btn"><X className="w-3 h-3" /> Cancel</button>
            {!isDefault && <button type="button" onClick={reset} className="ml-auto flex items-center gap-1 text-xs text-gray-500" data-testid="workflow-reset-btn"><RotateCcw className="w-3 h-3" /> Reset to default</button>}
          </div>
        </div>
      )}
    </div>
  );
};
